import React from 'react';
import { Collapse, CustomTag } from '../../components';
import { Driver } from '@bringg/types';
import RateWrapper from '../../components/rate/rate';
import BatteryIcon from '../../components/battery-icon/battery-icon';
import _noop from 'lodash/noop';
import classNames from 'classnames';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';

export interface Translations {
	driver: string;
	message: string;
	locate: string;
	unassignDriver: string;
	driverOnShift: string;
	driverOffShift: string;
	driverOnBreak: string;
	driverUnavailable?: string;
}

export enum DriverAction {
	MESSAGE,
	LOCATE,
	UNASSIGN
}

interface Props {
	driver?: Driver;
	onDriverAction?: (driverAction: DriverAction) => void;
	noDriverContent?: JSX.Element;
	translations: Translations;
	fleetName?: string;
	driverStatus?: DriverStatus;
	isTaskActive?: boolean;
	isAllowedToAssign?: boolean;
	showMessageDriverAction?: boolean;
}

export enum DriverStatus {
	OnShift = 'on-shift',
	OffShift = 'off-shift',
	OnBreak = 'on-break',
	Unavailable = 'unavailable'
}

const DriverAssignment: React.FC<Props> = ({
	driver = {} as Driver,
	onDriverAction = _noop,
	noDriverContent = null,
	translations,
	fleetName,
	driverStatus,
	isTaskActive,
	isAllowedToAssign = true,
	showMessageDriverAction = true
}) => {
	const { id, name, job_description, average_rating, profile_image, phone, battery } = driver;

	const handleDriverAction = (driverAction: DriverAction) => () => {
		if (driverAction === DriverAction.UNASSIGN && !isAllowedToAssign) {
			return;
		}
		return onDriverAction(driverAction);
	};

	const driverStatusToText = {
		[DriverStatus.OnShift]: translations.driverOnShift,
		[DriverStatus.OffShift]: translations.driverOffShift,
		[DriverStatus.OnBreak]: translations.driverOnBreak,
		[DriverStatus.Unavailable]: translations.driverUnavailable
	};

	let header = translations.driver;

	if (name) {
		header += ` (${name})`;
	}

	const showActions = showMessageDriverAction || isTaskActive || battery;

	return (
		<Collapse
			testId={'driver-assignment'}
			defaultActiveKey={'driver-assignment'}
			wrapClassName="driver-assignment-collapse"
		>
			<Collapse.Panel key="driver-assignment" header={header}>
				{id ? (
					<div className="driver-assignment-container">
						<div className="driver-assignment-header">
							<div>
								<a href={`/#/drivers/${driver.id}`} target="_blank">
									<div className="driver-assignment-name">{name}</div>
								</a>

								{fleetName && <CustomTag className="fleet-tag">{fleetName}</CustomTag>}

								<div className="driver-assignment-job-description">{job_description}</div>

								<RateWrapper disabled={true} allowHalf={true} value={average_rating} />

								{phone && (
									<div className="driver-assignment-phone">
										<BringgIcon iconName={BringgFontIcons.Phone} className="phone-icon" />
										<span>{phone}</span>
									</div>
								)}
							</div>

							<div className="driver-assignment-profile">
								<a className="driver-link" href={`/#/drivers/${driver.id}`} target="_blank">
									<img className="driver-assignment-avatar" src={profile_image} alt="driver-avatar" />
								</a>
								<div className={`driver-assignment-status driver-assignment-status-${driverStatus}`}>
									<span className="driver-assignment-status-text">
										{driverStatusToText[driverStatus]}
									</span>
								</div>
							</div>
						</div>

						{showActions && (
							<div className="driver-assignment-actions">
								{showMessageDriverAction && (
									<div
										className="driver-assignment-action message-driver-action"
										data-test-id="message-driver-action"
										onClick={handleDriverAction(DriverAction.MESSAGE)}
									>
										<BringgIcon iconName={BringgFontIcons.ChatBubble} />
										<span>{translations.message}</span>
									</div>
								)}

								{isTaskActive && (
									<div
										className="driver-assignment-action"
										onClick={handleDriverAction(DriverAction.LOCATE)}
									>
										<BringgIcon iconName={BringgFontIcons.Location} />
										<span>{translations.locate}</span>
									</div>
								)}

								{battery && (
									<div className="driver-assignment-battery">
										<BatteryIcon
											batteryPercentage={battery}
											className="driver-assignment-battery-icon"
											rotate90
										/>
										{battery}%
									</div>
								)}
							</div>
						)}

						{isTaskActive && (
							<div
								className={classNames('driver-assignment-unassign', {
									disabled: !isAllowedToAssign
								})}
								onClick={handleDriverAction(DriverAction.UNASSIGN)}
							>
								<BringgIcon iconName={BringgFontIcons.Close} className="close-icon" />
								{translations.unassignDriver}
							</div>
						)}
					</div>
				) : (
					<>{noDriverContent}</>
				)}
			</Collapse.Panel>
		</Collapse>
	);
};

export default DriverAssignment;
